import { GLIcon } from '@group-link-one/gl-icons';
import type { ISPMenuItem as IGLMenuItem } from '@group-link-one/gl-ui/sonarprint';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainRoutes } from '../enums/enums';
import { useMainLayoutTranslate } from './useMainLayoutTranslate';
import { usePrivileges } from '@/hooks/usePrivilleges';
import { verifyIfPathIsActives } from '@/utils/verifyIfPathIsActive';

export const useNavListTop = () => {
  const navigate = useNavigate();
  const { pages } = usePrivileges();
  const { customLocale } = useMainLayoutTranslate();

  const navListTop: IGLMenuItem[] = useMemo(() => {
    const allPages: IGLMenuItem[] = [
      // API Page
      {
        id: 'api',
        icon: <GLIcon variant='Misc.ElectricCurrent' />,
        label: customLocale('main.api', 'API'),
        active: verifyIfPathIsActives(MainRoutes.API),
        isToAppear: pages.api,
        onClick: () => navigate(MainRoutes.API),
      },
      // Reports Page
      {
        id: 'reports',
        icon: <GLIcon variant='Misc.ChartBar' />,
        label: customLocale('main.reports', 'Reports'),
        active: verifyIfPathIsActives(MainRoutes.REPORTS),
        isToAppear: pages.reports,
        onClick: () => navigate(MainRoutes.REPORTS),
      },
      // Groups Page
      {
        id: 'groups',
        icon: <GLIcon variant='Users.UsersThree' />,
        label: customLocale('main.groups.title', 'Groups'),
        active: verifyIfPathIsActives(MainRoutes.GROUPS),
        isToAppear: pages.groups,
        onClick: () => navigate(MainRoutes.GROUPS),
      },
      // Device Blocks Page
      {
        id: 'device-blocks',
        icon: <GLIcon variant='Misc.LockKey' />,
        label: customLocale('main.deviceBlocks', 'Device Blocks'),
        active: verifyIfPathIsActives(MainRoutes.DEVICE_BLOCKS),
        isToAppear: pages.deviceBlocks && !pages.tagsManagement, // Device Blocks is hidden when Tags Management is enabled
        onClick: () => navigate(MainRoutes.DEVICE_BLOCKS),
      },
      // Tags Management Page
      {
        id: 'tags-management',
        icon: <GLIcon variant='Misc.Tag' />,
        label: customLocale('main.tagsManagement', 'Tags Management'),
        active: verifyIfPathIsActives(MainRoutes.TAGS_MANAGEMENT),
        isToAppear: pages.tagsManagement,
        isNew: true,
        onClick: () => navigate(MainRoutes.TAGS_MANAGEMENT),
      },
      // Developer Page
      {
        id: 'developer',
        icon: <GLIcon variant='GL.GLSDK' />,
        label: customLocale('developer.title', 'Developer'),
        active: verifyIfPathIsActives(MainRoutes.DEVELOPER),
        isToAppear: pages.developer.sdk || pages.developer.batch || pages.developer.credentials,
        dropdown: true,
        items: [
          {
            id: 'developer_credentials',
            label: customLocale('developer.credentials', 'Credentials'),
            active: verifyIfPathIsActives(MainRoutes.DEVELOPER_CREDENTIALS, true),
            isToAppear: pages.developer.credentials,
            onClick: () => navigate(MainRoutes.DEVELOPER_CREDENTIALS),
          },
          {
            id: 'developer_batch',
            label: customLocale('developer.batch', 'Batch'),
            active: verifyIfPathIsActives(MainRoutes.DEVELOPER_BATCH, true),
            isToAppear: pages.developer.batch,
            onClick: () => navigate(MainRoutes.DEVELOPER_BATCH),
          },
          {
            id: 'developer_sdk',
            label: customLocale('developer.sdk', 'SDK'),
            active: verifyIfPathIsActives(MainRoutes.DEVELOPER_SDK, true),
            isToAppear: pages.developer.sdk,
            onClick: () => navigate(MainRoutes.DEVELOPER_SDK),
          },
        ],
      },
    ];

    return allPages;
  }, [pages, customLocale, navigate]);

  return { navListTop };
};
